import { ACQUISITION_SOURCES, asPatientId, type PatientId } from "@repo/domain";
import { z } from "zod";
import { addressSchema, contactPointSchema, humanNameSchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";

import { registrationRelatedPersonSchema } from "./related-person-details.schemas";

export const patientIdSchema: z.ZodType<PatientId, z.ZodTypeDef, string> = z.string().min(1).transform(asPatientId);

/** Domain-facing Patient reference; the id is stable identity, never a FHIR row locator. */
export const patientRefSchema = z.object({ resourceType: z.literal("Patient"), id: patientIdSchema });

export const caseAcquisitionSchema = z.object({ source: z.enum(ACQUISITION_SOURCES) }).strict();

/** Register one patient per source identity, independently of the source system. */
export const registerPatientInputSchema = z.object({
  acquisition: caseAcquisitionSchema.optional(),
  relatedPerson: registrationRelatedPersonSchema.optional(),
  caseId: newEntityIdSchema,
  reviewTaskId: newEntityIdSchema,
  id: newEntityIdSchema,
  sourceIdentity: z.object({ system: z.string().trim().min(1), value: z.string().trim().min(1) }),
  name: z.array(humanNameSchema).optional(),
  birthDate: z.string().optional(),
  gender: z.enum(["male", "female", "other", "unknown"]).optional(),
  telecom: z.array(contactPointSchema).optional(),
  address: z.array(addressSchema).optional(),
});

export type RegisterPatientInput = z.infer<typeof registerPatientInputSchema>;

/** Sync patient input — update existing Patient demographics */
export const syncPatientInputSchema = z.object({
  identifierSystem: z.string().min(1),
  identifierValue: z.string().min(1),
  name: z.array(humanNameSchema).optional(),
  birthDate: z.string().optional(),
  gender: z.enum(["male", "female", "other", "unknown"]).optional(),
  telecom: z.array(contactPointSchema).optional(),
  address: z.array(addressSchema).optional(),
});

export type SyncPatientInput = z.infer<typeof syncPatientInputSchema>;

export const appRegisterPatientInputSchema = registerPatientInputSchema
  .omit({ sourceIdentity: true, acquisition: true })
  .extend({
    name: z
      .array(
        humanNameSchema
          .extend({
            given: z.array(z.string().trim().min(1, "Enter a first name")).min(1, "Enter a first name"),
            family: z.string().trim().min(1, "Enter a last name").optional(),
          })
          .refine((name) => name.use === "usual" || !!name.family, {
            message: "Enter a last name",
            path: ["family"],
          }),
      )
      .min(1),
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Use a complete date of birth")
      .refine((value) => {
        const date = new Date(`${value}T00:00:00Z`);
        return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
      }, "Enter a valid date of birth")
      .refine((value) => value <= new Date().toISOString().slice(0, 10), "Date of birth cannot be in the future"),
    gender: z.enum(["male", "female", "other", "unknown"]),
    telecom: z
      .array(contactPointSchema.extend({ value: z.string().trim().min(1) }))
      .refine(
        (points) =>
          points.every((point) => point.system !== "email" || z.string().email().safeParse(point.value).success),
        "Enter a valid email",
      )
      .optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (!input.relatedPerson && !input.telecom?.some((point) => point.system === "email" && point.value))
      ctx.addIssue({ code: "custom", path: ["telecom"], message: "Enter a patient email or add a related person" });
  });
export const resolveRegistrationInputSchema = z.discriminatedUnion("kind", [
  z.object({
    caseId: newEntityIdSchema,
    taskId: z.string().min(1),
    kind: z.literal("link"),
    patientId: patientIdSchema,
  }),
  z.object({
    taskId: z.string().min(1),
    caseId: newEntityIdSchema,
    kind: z.literal("different-person"),
    uncheckedCandidateIndexes: z.array(z.number().int().nonnegative()).optional(),
  }),
]);
export type ResolveRegistrationInput = z.infer<typeof resolveRegistrationInputSchema>;
